<template>
	<div class="MediaCredit" :class="classes">
		<!--check title-->
		<div v-if="title" class="MediaCredit-title" v-html="title"></div>

		<!--check caption-->
		<p v-if="caption" class="MediaCredit-caption" v-html="caption"></p>

		<!--check commonName-->
		<div v-if="commonName">
			<div v-if="valid === 'false'">
				<div class="Unconfirmed" data-tooltip title="Unconfirmed">
					<CloIcon symbol="warning" />
				</div>
			</div>

			<div class="MediaCredit-commonName">
				{{ commonName }}
				<span class="MediaCredit-sciName">{{ sciName }}</span>
			</div>
		</div>

		<div v-if="copyright" class="MediaCredit-copyright">
			<span class="MediaCredit-icon">
				<CloIcon symbol="user" />
			</span>
			&copy;&nbsp;{{ copyright }}
		</div>

		<!-- location and date -->
		<ul v-if="location || obsDttm" class="InlineList InlineList--divided MediaCredit-meta">
			<li v-if="location">
				<span class="MediaCredit-icon">
					<CloIcon symbol="locationGeneric" />
				</span>
				{{ location }}
			</li>
			<li v-if="obsDttm">
				<span class="MediaCredit-icon">
					<CloIcon symbol="date" />
				</span>
				<time :datetime="obsDttmISO">{{ obsDttm }}</time>
			</li>
		</ul>

		<ul class="InlineList InlineList--divided MediaCredit-links">
			<li v-if="!noLinks">
				<a :href="citationUrl">
					<span class="MediaCredit-icon">
						<CloIcon symbol="newTab" />
					</span>
					<span>
						Macaulay Library
						<template v-if="showId">{{ citationName }}</template>
					</span>
				</a>
			</li>
			<li v-else>
				<span>
					<span class="MediaCredit-icon">
						<CloIcon symbol="newTab" />
					</span>
					<span>
						Macaulay Library
						<template v-if="showId">{{ citationName }}</template>
					</span>
				</span>
			</li>

			<!-- ebird checklist -->
			<template v-if="ebirdChecklistId">
				<li v-if="!noLinks">
					<a :href="'https://ebird.org/checklist/' + ebirdChecklistId">
						<span class="MediaCredit-icon">
							<CloIcon symbol="checklist" />
						</span>
						<span>eBird</span>
						<span v-if="showId">{{ ebirdChecklistId }}</span>
					</a>
				</li>
				<li v-else>
					<span>
						<span class="MediaCredit-icon">
							<CloIcon symbol="checklist" />
						</span>
						<span>eBird</span>
						<span v-if="showId">Checklist {{ ebirdChecklistId }}</span>
					</span>
				</li>
			</template>

			<!--check lablogo-->
			<li v-if="logoLab">
				<a href="https://birds.cornell.edu/" class="MediaCredit-logoLab">
					<CloLogoLabShort size="xs" :reverse="reverse" />
				</a>
			</li>
		</ul>
	</div>
</template>

<script>
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloLogoLabShort from '@/logos/CloLogoLabShort/CloLogoLabShort.vue';
import CloInlineList from '@/base/CloInlineList/CloInlineList.vue';

export default {
	components: {
		CloIcon,
		CloLogoLabShort,
		CloInlineList,
	},
	data() {
		return {};
	},
	computed: {
		classes() {
			return {
				'MediaCredit--inline': this.inline,
				'MediaCredit--overlay': this.overlay,
				'MediaCredit--right': this.right,
				'MediaCredit--reverse': this.reverse,
				'MediaCredit--sm': this.size === 'sm',
			};
		},
	},
	props: {
		/**
		 * Download URL for the media
		 */
		mediaDownloadUrl: String,
		/**
		 * Asset identification
		 */
		copyright: String,
		citationUrl: String,
		citationName: String,
		ebirdChecklistId: String,
		commonName: String,
		sciName: String,
		location: String,
		obsDttm: String,
		obsDttmISO: String,
		linksToggled: Boolean,
		/**
		 * To show the ID of media or not
		 */
		showId: Boolean,
		/**
		 * Reference location of links
		 */
		href: String,
		/**
		 * Text to add
		 */
		text: String,
		citationNewTab: Boolean,
		noLinks: {
			type: Boolean,
			default: false,
		},
		/**
		 * Caption to add
		 */
		caption: String,
		/**
		 * Title of the component
		 */
		title: String,
		/**
		 * Checks for validity
		 */
		valid: Boolean,
		/**
		 * To add the logolab or not
		 */
		logoLab: Boolean,
		inline: {
			type: Boolean,
			default: false,
		},
		right: {
			type: Boolean,
			default: false,
		},
		overlay: {
			type: Boolean,
			default: false,
		},
		reverse: {
			type: Boolean,
			default: false,
		},
		size: {
			type: String,
			default: null,
		},
	},
};
</script>

<style lang="scss">
.MediaCredit {
	@include stack-md;
	text-align: left;
	[dir='rtl'] & {
		text-align: right;
	}
}

.MediaCredit-icon {
	display: none;
	margin-right: 0;
	[dir='rtl'] & {
		margin-left: 0;
	}
	.Icon {
		@include icon-sm;
		fill: $color-link;
	}
}

.MediaCredit-logoLab {
	line-height: 1.5;
}

.MediaCredit-enlarge {
	@include text(2);
	float: right;
	.Icon {
		@include icon-sm;
	}
}

.MediaCredit-links {
	align-items: center;
	display: inline-flex;
	@include text(2);
	margin: 0;
}

.MediaCredit-copyright {
	@include text(2);
	margin: $space-stack-xs;
	margin-right: $space-md;
	[dir='rtl'] & {
		margin-left: $space-xs;
		margin-right: $space-md;
	}
}

.MediaCredit-title {
	@include text(3);
	font-weight: bold;
	margin: $space-stack-xs;
}

.MediaCredit-caption {
	@include text(2);
	margin: $space-stack-xs;
}

.MediaCredit-commonName {
	@include text(3);
	line-height: 1.2;
	font-weight: bold;
	margin: $space-stack-xs;
	margin-right: $space-md;
	[dir='rtl'] & {
		margin-left: $space-xs;
		margin-right: $space-md;
	}
	.MediaCredit-sciName {
		@include text(2);
		font-weight: normal;
		font-style: italic;
		margin-left: $space-xs;
		[dir='rtl'] & {
			margin-left: 0;
			margin-right: $space-xs;
		}
	}
}

.MediaCredit-meta {
	@include text(2);
	margin: $space-stack-xs;
}

.MediaCredit-toggler {
	@include text(2);
	@include inset-xs;
	display: none;
}

// inline variant
.MediaCredit--inline {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-start;
	align-items: center;
	.MediaCredit-commonName {
		@include text(2);
	}
	.MediaCredit-commonName,
	.MediaCredit-copyright,
	.MediaCredit-links {
		margin-bottom: 0;
	}
	.MediaCredit-meta {
		@include inline-md;
	}
	.MediaCredit-title,
	.MediaCredit-caption {
		flex: 1 0 100%;
	}
}

// inline aligned to right side
.MediaCredit--inlineJustifyRight {
	justify-content: flex-end;
}

// display icons
.MediaCredit--icons {
	.MediaCredit-icon {
		display: inline;
	}
}

// large style with icons
.MediaCredit--large {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	// height: 100%;
	.InlineList li {
		@include stack-sm;
		align-items: center;
		display: flex;
		padding: 0;
		border: 0;
		a {
			align-items: center;
			display: flex;
		}
	}
	.MediaCredit-icon {
		@include inline-sm;
		display: inline;
		.Icon {
			@include icon-sm;
			fill: $color-neutral-2;
		}
	}
	a .MediaCredit-icon .Icon {
		fill: currentColor;
	}
	.MediaCredit-commonName {
		@include stack-sm;
		flex: 1 1 100%; /* consume a full row */
		font-size: 1rem;
		.MediaCredit-sciName {
			@include text(3);
			display: block;
			margin: 0;
		}
	}
	.MediaCredit-copyright {
		@include stack-sm;
		flex: 1 1 100%; /* consume a full row */
	}
	.MediaCredit-links {
		// is this the best way to do this?
		margin-top: auto;
	}

	.MediaCredit-meta {
		flex: 0 1 auto;
		margin: 0 $space-lg $space-sm 0;
		[dir='rtl'] & {
			margin: 0 0 $space-sm $space-lg;
		}
	}

	.MediaCredit-links {
		flex: 0 1 auto;
		margin: 0;
	}
}

// reverse colors
.MediaCredit--reverse {
	color: $color-text-reverse;
	.MediaCredit-title {
		color: $color-text-reverse;
	}
	.MediaCredit-caption {
		color: $color-text-muted-reverse;
	}
	.MediaCredit-icon .Icon {
		fill: $color-link-reverse;
	}
	a {
		color: $color-text-reverse;
		&:hover {
			color: $color-link-hover-reverse;
		}
	}
	.MediaCredit-commonName .MediaCredit-sciName {
		color: $color-text-muted-reverse;
	}
	.MediaCredit-links {
		li {
			border-right-color: transparentize($color-white, 0.8);
		}
	}
}

.MediaCredit--fixedFontSize {
	.MediaCredit-enlarge,
	.MediaCredit-links,
	.MediaCredit-copyright,
	.MediaCredit-commonName .MediaCredit-sciName,
	.MediaCredit-meta {
		font-size: 12px;
	}

	.MediaCredit-title,
	.MediaCredit-caption,
	.MediaCredit-commonName {
		font-size: 14px;
	}
}

.MediaCredit--toggled-sm {
	@include breakpoint(mediumsmall, down) {
		position: relative;
		.MediaCredit-toggler {
			display: inline-block;
			position: absolute;
			@include u-topRight;
		}
		.MediaCredit-links[aria-hidden='true'] {
			display: none;
		}
		// when using inline variant AND toggled, need these fixes
		&.MediaCredit--inline {
			.MediaCredit-toggler {
				padding: 0;
			}
			.MediaCredit-meta {
				display: inline-block;
				margin-right: 4ch;
				[dir='rtl'] & {
					margin-left: 4ch;
					margin-right: 0;
				}
			}
		}
	}
}

.MediaCredit--overlay {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	background: $color-overlay-black;
	@include inset-xs;
	margin: 0;
}

.MediaCredit-right {
	margin-right: 0; // remove inline spacing when right aligned
	margin-left: auto;
	[dir='rtl'] & {
		margin-left: 0;
		margin-right: auto;
	}
	@include text(2);
}

.MediaCredit--heroOverlayRight {
	@include breakpoint(medium) {
		justify-content: flex-end;
	}
	padding-right: 11rem;
	[dir='rtl'] & {
		padding-left: 0;
		padding-right: 11rem;
	}
}

.MediaCredit--sm {
	font-size: 10px;
	.MediaCredit-links,
	.MediaCredit-copyright,
	.MediaCredit-title,
	.MediaCredit-caption,
	.MediaCredit-commonName,
	.MediaCredit-commonName .MediaCredit-sciName,
	.MediaCredit-meta,
	.MediaCredit-toggler,
	.MediaCredit-right {
		font-size: 10px;
	}
}
</style>
